import {
  CapsuleGeometry,
  CanvasTexture,
  Color,
  Group,
  Mesh,
  MeshLambertMaterial,
  Scene,
  Sprite,
  SpriteMaterial,
  Vector3,
} from 'three';
import type { Vector3D } from '../types';

const HP_BAR_WIDTH = 96;
const HP_BAR_HEIGHT = 28;
const PEER_COLORS = [0x5ec8ff, 0xff8a5c, 0x9d7bff, 0x7dffb3, 0xffd166];

interface RemotePeer {
  playerId: string;
  displayName: string;
  group: Group;
  body: Mesh;
  target: Vector3;
  yaw: number;
  health: number;
  maxHealth: number;
  lastSeen: number;
  hpSprite: Sprite;
  hpCanvas: HTMLCanvasElement;
  hpCtx: CanvasRenderingContext2D;
  hpTexture: CanvasTexture;
}

/**
 * Lightweight remote player visuals — capsules + name/HP billboards.
 */
export class RemotePlayerManager {
  private readonly scene: Scene;
  private readonly peers = new Map<string, RemotePeer>();
  private readonly localPlayerId: string;
  private colorIndex = 0;

  constructor(scene: Scene, localPlayerId: string) {
    this.scene = scene;
    this.localPlayerId = localPlayerId;
  }

  upsert(
    playerId: string,
    displayName: string,
    position?: Vector3D,
    yaw = 0,
    health?: number,
    maxHealth?: number,
  ): void {
    if (playerId === this.localPlayerId) return;
    let peer = this.peers.get(playerId);
    if (!peer) {
      const color = new Color(PEER_COLORS[this.colorIndex++ % PEER_COLORS.length]!);
      const group = new Group();
      const body = new Mesh(
        new CapsuleGeometry(0.32, 0.95, 4, 8),
        new MeshLambertMaterial({ color }),
      );
      body.position.y = 0.9;
      group.add(body);

      const { hpSprite, hpCanvas, hpCtx, hpTexture } = createPlayerBillboard();
      group.add(hpSprite);
      this.scene.add(group);

      peer = {
        playerId,
        displayName: displayName || 'Miner',
        group,
        body,
        target: new Vector3(position?.x ?? 0, position?.y ?? 0, position?.z ?? 0),
        yaw,
        health: health ?? 100,
        maxHealth: maxHealth ?? 100,
        lastSeen: performance.now(),
        hpSprite,
        hpCanvas,
        hpCtx,
        hpTexture,
      };
      if (position) group.position.copy(peer.target);
      this.peers.set(playerId, peer);
      this.paintBillboard(peer);
    } else {
      peer.displayName = displayName || peer.displayName;
      if (position) peer.target.set(position.x, position.y, position.z);
      peer.yaw = yaw;
      if (health !== undefined) peer.health = health;
      if (maxHealth !== undefined && maxHealth > 0) peer.maxHealth = maxHealth;
      peer.lastSeen = performance.now();
      this.paintBillboard(peer);
    }
  }

  applyMove(
    playerId: string,
    position: Vector3D,
    yaw = 0,
    health?: number,
    maxHealth?: number,
  ): void {
    if (playerId === this.localPlayerId) return;
    const peer = this.peers.get(playerId);
    if (!peer) {
      this.upsert(playerId, 'Miner', position, yaw, health, maxHealth);
      return;
    }
    peer.target.set(position.x, position.y, position.z);
    peer.yaw = yaw;
    if (health !== undefined) peer.health = health;
    if (maxHealth !== undefined && maxHealth > 0) peer.maxHealth = maxHealth;
    peer.lastSeen = performance.now();
    this.paintBillboard(peer);
  }

  setHealth(playerId: string, health: number, maxHealth?: number): void {
    const peer = this.peers.get(playerId);
    if (!peer) return;
    peer.health = health;
    if (maxHealth !== undefined && maxHealth > 0) peer.maxHealth = maxHealth;
    this.paintBillboard(peer);
  }

  remove(playerId: string): void {
    const peer = this.peers.get(playerId);
    if (!peer) return;
    this.scene.remove(peer.group);
    peer.hpTexture.dispose();
    (peer.hpSprite.material as SpriteMaterial).dispose();
    peer.body.geometry.dispose();
    const mat = peer.body.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat.dispose();
    this.peers.delete(playerId);
  }

  update(dt: number): void {
    const now = performance.now();
    for (const peer of this.peers.values()) {
      if (now - peer.lastSeen > 15000) {
        this.remove(peer.playerId);
        continue;
      }
      peer.group.position.lerp(peer.target, Math.min(1, dt * 12));
      // Keep body facing move yaw; billboard sprite always faces camera.
      peer.body.rotation.y = peer.yaw;
    }
  }

  /** Living remote peers for host enemy chase / contact. */
  listTargets(): Array<{ id: string; position: Vector3D; alive: boolean }> {
    const out: Array<{ id: string; position: Vector3D; alive: boolean }> = [];
    for (const peer of this.peers.values()) {
      out.push({
        id: peer.playerId,
        position: {
          x: peer.target.x,
          y: peer.target.y,
          z: peer.target.z,
        },
        alive: peer.health > 0,
      });
    }
    return out;
  }

  listHealth(): Array<{ playerId: string; displayName: string; health: number; maxHealth: number }> {
    return [...this.peers.values()].map((p) => ({
      playerId: p.playerId,
      displayName: p.displayName,
      health: p.health,
      maxHealth: p.maxHealth,
    }));
  }

  clear(): void {
    for (const id of [...this.peers.keys()]) {
      this.remove(id);
    }
  }

  dispose(): void {
    this.clear();
  }

  private paintBillboard(peer: RemotePeer): void {
    const { hpCtx: ctx, hpCanvas: canvas, hpTexture } = peer;
    const w = canvas.width;
    const h = canvas.height;
    const frac = Math.max(0, Math.min(1, peer.health / Math.max(1, peer.maxHealth)));

    ctx.clearRect(0, 0, w, h);

    // Name
    ctx.font = 'bold 12px ui-monospace, Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.fillStyle = '#e8eef5';
    const label = peer.displayName.slice(0, 16);
    ctx.strokeText(label, w / 2, 1);
    ctx.fillText(label, w / 2, 1);

    // HP bar
    const barY = 15;
    const barH = 10;
    const padX = 6;
    ctx.fillStyle = 'rgba(0,0,0,0.75)';
    ctx.fillRect(padX, barY, w - padX * 2, barH);
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(padX + 0.5, barY + 0.5, w - padX * 2 - 1, barH - 1);

    const innerPad = 2;
    const innerW = w - padX * 2 - innerPad * 2;
    const fillW = Math.max(0, Math.floor(innerW * frac));
    const r = frac > 0.5 ? Math.round(255 * (1 - (frac - 0.5) * 2)) : 255;
    const g = frac > 0.5 ? 200 : Math.round(200 * (frac / 0.5));
    ctx.fillStyle = `rgb(${r},${g},48)`;
    ctx.fillRect(padX + innerPad, barY + innerPad, fillW, barH - innerPad * 2);

    hpTexture.needsUpdate = true;
    peer.hpSprite.visible = true;
  }
}

function createPlayerBillboard(): {
  hpSprite: Sprite;
  hpCanvas: HTMLCanvasElement;
  hpCtx: CanvasRenderingContext2D;
  hpTexture: CanvasTexture;
} {
  const canvas = document.createElement('canvas');
  canvas.width = HP_BAR_WIDTH;
  canvas.height = HP_BAR_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d canvas unavailable for player HP bars');
  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  const spriteMat = new SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: true,
    sizeAttenuation: true,
  });
  const hpSprite = new Sprite(spriteMat);
  hpSprite.scale.set(1.35, 0.4, 1);
  hpSprite.position.y = 2.15;
  hpSprite.name = 'playerHp';
  return { hpSprite, hpCanvas: canvas, hpCtx: ctx, hpTexture: texture };
}
